import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { Box, Typography } from '@mui/material';
import * as Reorder from '../dnd/Reorder';
import { IBriefProfession } from './constants';
import { useProfessions } from '../../hooks/useProfessions';
import { IconPlus, IconPencil, IconTrash } from '../../utils/icons';
import { SelectPanel } from '../../pages/pro/createEditProfile/formFragments/SelectPanel';
import { useField } from 'formik';
import { FieldError } from '../fields/FieldError/FieldError';
import { useMergePendingItems } from '../../hooks/useMergePendingItems';

interface Props {
  professions: IBriefProfession[];
  updateProfessions: (professions: IBriefProfession[]) => void;
  inputProps?: any;
  onProfessionUpdate?: (professions: IBriefProfession[]) => void;
}

interface SingleProfessionInputProps {
  profession?: IBriefProfession;
  onChange: (profession: IBriefProfession) => void;
  onCross?: () => void;
  inputProps?: any;
  onAddToPending?: (profession: IBriefProfession) => void;
}

const SingleProfessionInput = forwardRef<{ getCurrentValue: () => IBriefProfession | null; clear: () => void }, SingleProfessionInputProps>(({
  profession: professionProp,
  onChange,
  onCross,
  inputProps,
  onAddToPending,
  ...rest
}, ref) => {
  const { professionsList, subProfessionLibrary } = useProfessions();
  const [profession, setProfession] = useState<{ id: number; name: string } | undefined>();
  const [subProfession, setSubProfession] = useState<{ id: number; name: string } | undefined>();
  const [showInputPanel, setShowInputPanel] = useState(true);

  useImperativeHandle(ref, () => ({
    getCurrentValue: () => {
      if (!profession) return null;
      return {
        profession_id: profession.id,
        sub_profession_id: subProfession?.id,
        order: professionProp?.order || 0,
      };
    },
    clear: () => {
      setProfession(undefined);
      setSubProfession(undefined);
      setShowInputPanel(false);
      setTimeout(() => setShowInputPanel(true), 10);
    }
  }));

  React.useEffect(() => {
    if (professionProp) {
      const prof = professionsList.find(p => p.id === professionProp.profession_id);
      setProfession(prof);
      
      if (professionProp.sub_profession_id) {
        const subProf = subProfessionLibrary[professionProp.profession_id]?.find(
          (sp: any) => sp.id === professionProp.sub_profession_id
        );
        setSubProfession(subProf);
      }
    }
  }, [professionProp, professionsList, subProfessionLibrary]);

  const onProfessionChange = (value: { id: number; name: string }) => {
    setProfession(value);
    setSubProfession(undefined);
  };

  const subProfessionList = React.useMemo(() => {
    const index = profession?.id || -1;
    return subProfessionLibrary[index] || [];
  }, [profession, subProfessionLibrary]);

  const onSubProfessionChange = (value: { id: number; name: string }) => {
    if (profession) setSubProfession(value);
  };

  const onSubmit = () => {
    if (!profession) return;
  
    const output: IBriefProfession = {
      profession_id: profession.id,
      sub_profession_id: subProfession?.id,
      order: professionProp?.order || 0,
    };

    if (onAddToPending) {
      onAddToPending(output);
    } else {
      onChange(output);
    }
    
    setProfession(undefined);
    setSubProfession(undefined);
    setShowInputPanel(false);
    setTimeout(() => setShowInputPanel(true), 10);
  };

  return (
    <Box
      {...rest}
      {...inputProps}
      className="flex max-sm:flex-col max-sm:items-stretch max-sm:h-auto gap-2 items-end justify-between p-4 border border-border-token rounded-lg h-[105px]"
    >
      {showInputPanel && (
        <>
          <Box className="flex gap-2 flex-col w-full">
            <Typography variant="body1" color="var(--text)">
              Profession
            </Typography>
            <SelectPanel
              value={profession}
              onChange={onProfessionChange}
              placeholder="Select profession"
              objectOptions={professionsList.map((prof) => ({
                label: prof.name,
                value: prof,
              }))}
            />
          </Box>
          <Box className="flex gap-2 flex-col w-full">
            <Typography variant="body1" color="var(--text)">
              Sub-category
            </Typography>
            <SelectPanel
              value={subProfession}
              onChange={onSubProfessionChange}
              placeholder="Select sub-category"
              objectOptions={subProfessionList.map((prof: any) => ({
                label: prof.name,
                value: prof,
              }))}
              disabled={!profession}
            />
          </Box>
          <Box className="flex gap-2 items-center">
            <Box
              className="flex items-center justify-center cursor-pointer p-2 bg-accent text-white rounded-lg"
              onClick={onSubmit}
            >
              <IconPlus width={18} height={18} />
            </Box>
            {onCross && (
              <Box
                className="flex items-center justify-center cursor-pointer p-2 bg-surface-2 text-text-1 rounded-lg"
                onClick={onCross}
              >
                {/* stroke via currentColor — SVG presentation attributes can't read var() */}
                <IconTrash width={18} height={18} stroke="currentColor" />
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
});

interface SingleProfessionEntryProps {
  index: number;
  profession: IBriefProfession;
  onEditProfession: (index: number) => void;
  onRemoveProfession: (index: number) => void;
}

const SingleProfessionEntry: React.FC<SingleProfessionEntryProps> = ({
  index,
  profession,
  onEditProfession,
  onRemoveProfession,
}) => {
  const { professionsList, subProfessionLibrary } = useProfessions();
  const containerRef = React.useRef(null);

  const professionName = professionsList.find(p => p.id === profession.profession_id)?.name || '-';
  const subProfessionName = profession.sub_profession_id 
    ? subProfessionLibrary[profession.profession_id]?.find((sp: any) => sp.id === profession.sub_profession_id)?.name || '-'
    : '-';

  return (
    <Box
      ref={containerRef}
      key={`profession-${index}`}
      className="flex gap-2 items-center justify-between p-4 border border-border-token rounded-lg"
    >
      <Typography className="min-w-[fit-content]" fontFamily="CircularStdBlack">
        {professionName}
      </Typography>
      <Typography className="w-full text-text-2 text-center">
        {subProfessionName}
      </Typography>
      <Box className="flex gap-2 items-center">
        {/* stroke via currentColor — SVG presentation attributes can't read var() */}
        <Box
          className="flex items-center justify-center cursor-pointer p-1 text-muted"
          onClick={() => onEditProfession(index)}
        >
          <IconPencil size={18} stroke="currentColor" />
        </Box>
        <Box
          className="flex items-center justify-center cursor-pointer p-1 text-muted"
          onClick={() => onRemoveProfession(index)}
        >
          <IconTrash width={18} height={18} stroke="currentColor" />
        </Box>
        <Reorder.Trigger index={index} containerRef={containerRef}>
          <Box className="flex items-center justify-center cursor-pointer">
            <div className="w-6 h-6 flex items-center justify-center">
              <div className="w-1 h-4 bg-muted rounded-full"></div>
            </div>
          </Box>
        </Reorder.Trigger>
      </Box>
    </Box>
  );
};

export const BriefProfessions = forwardRef<{ 
  mergePendingItems: () => IBriefProfession[];
  hasPendingItems: () => boolean;
}, Props>(({
  professions = [],
  updateProfessions,
  inputProps = {},
  onProfessionUpdate,
}, ref) => {
  const [editableIndex, setEditableIndex] = useState(-1);
  const [field, meta] = useField('professions');
  const { professionsList, subProfessionLibrary } = useProfessions();
  const inputRef = useRef<{ getCurrentValue: () => IBriefProfession | null; clear: () => void }>(null);

  const isDuplicateProfession = (profession: IBriefProfession, list: IBriefProfession[]): boolean => {
    return list.some(existing => 
      existing.profession_id === profession.profession_id &&
      (existing.sub_profession_id === profession.sub_profession_id || 
       (!existing.sub_profession_id && !profession.sub_profession_id))
    );
  };

  const { pendingItems, mergePendingItems, addToPending, clearPending } = useMergePendingItems<IBriefProfession, IBriefProfession>({
    items: professions,
    updateItems: updateProfessions,
    inputRef,
    mergeOrder: 'prepend',
    currentValuePosition: 'first',
    isDuplicate: isDuplicateProfession,
  });

  const hasPendingItems = (): boolean => {
    const currentValue = inputRef.current?.getCurrentValue();
    return pendingItems.length > 0 || (currentValue !== null && currentValue !== undefined);
  };

  useImperativeHandle(ref, () => ({
    mergePendingItems,
    hasPendingItems,
  }));

  const handleAddToPending = (profession: IBriefProfession) => {
    if (isDuplicateProfession(profession, pendingItems) || isDuplicateProfession(profession, professions)) {
      return;
    }
    const newProfessions = [...professions, profession];
    updateProfessions(newProfessions);
    clearPending();
  };

  const addSingleProfession = (profession: IBriefProfession) => {
    if (isDuplicateProfession(profession, professions)) {
      return;
    }
    const allItems = [...pendingItems, profession];
    const newProfessions = [...allItems, ...professions];
    updateProfessions(newProfessions);
    clearPending();
    setEditableIndex(-1);
    
    if (onProfessionUpdate) {
      onProfessionUpdate(newProfessions);
    }
  };

  const updateSingleProfession = (profession: IBriefProfession, index: number) => {
    const tempList = [...professions];
    tempList[index] = profession;
    const newProfessions = [...tempList];
    updateProfessions(newProfessions);
    setEditableIndex(-1);
    
    if (onProfessionUpdate) {
      onProfessionUpdate(newProfessions);
    }
  };

  const removeSingleProfession = (index: number) => {
    const tempList = [...professions];
    tempList.splice(index, 1);
    const newProfessions = [...tempList];
    updateProfessions(newProfessions);
    
    if (onProfessionUpdate) {
      onProfessionUpdate(newProfessions);
    }
  };

  return (
    <Box className="flex flex-col gap-2 w-full">
      <SingleProfessionInput 
        ref={inputRef}
        {...inputProps} 
        onChange={addSingleProfession}
        onAddToPending={handleAddToPending}
      />

      {pendingItems.length > 0 && (
        <Box className="flex flex-col gap-2 w-full mb-2">
          {pendingItems.map((item, index) => {
            const professionName = professionsList.find(p => p.id === item.profession_id)?.name || '-';
            const subProfessionName = item.sub_profession_id 
              ? subProfessionLibrary[item.profession_id]?.find((sp: any) => sp.id === item.sub_profession_id)?.name || '-'
              : '-';
            return (
              <Box key={`pending-${index}`} className="p-2 border border-dashed border-border-token rounded-lg bg-surface-2">
                <Typography className="text-text-1">
                  {professionName}
                  {subProfessionName !== '-' && ` - ${subProfessionName}`}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      <Reorder.Root options={professions} onUpdateOrder={updateProfessions}>
        {professions.map((item: IBriefProfession, index: number) => {
          if (editableIndex === index) {
            return (
              <SingleProfessionInput
                {...inputProps}
                key={`profession-${item.profession_id}-${index}`}
                profession={item}
                onChange={(text) => updateSingleProfession(text, index)}
                onCross={() => removeSingleProfession(index)}
              />
            );
          }

          return (
            <SingleProfessionEntry
              key={index}
              index={index}
              profession={item}
              onEditProfession={setEditableIndex}
              onRemoveProfession={removeSingleProfession}
            />
          );
        })}
      </Reorder.Root>
      {!!meta.touched && !!meta.error && <FieldError>{meta.error}</FieldError>}
    </Box>
  );
}); 